import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { Organization } from '../models/Organization';
import { User } from '../models/User';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

export type OnboardingStep = 'initial' | 'create' | 'join' | 'profile' | 'complete';

interface OnboardingContextType {
  currentStep: OnboardingStep;
  isOnboardingComplete: boolean;
  loading: boolean;
  startOnboarding: () => void;
  setCurrentStep: (step: OnboardingStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  handleCreateOrganization: (organization: Organization) => Promise<void>;
  handleJoinOrganization: (code: string) => Promise<void | {
    orgId: string;
    organizationType: string;
    organizationName: string;
    organizationDescription?: string;
    baseAirport?: string;
    joinCode: string;
    requiresApproval: boolean;
  }>;
  handleUpdateProfile: (user: Partial<User>) => Promise<void>;
  completeOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('initial');
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const initializedUserIdRef = useRef<string | null>(null);
  const isUpdatingRef = useRef(false); // Flag to prevent useEffect from interfering during updates
  const lastEvaluatedValuesRef = useRef<{
    userId: string | null;
    orgId: string | null | undefined;
    firstName: string | null | undefined;
    lastName: string | null | undefined;
  }>({ userId: null, orgId: null, firstName: null, lastName: null });
  const navigate = useNavigate();
  const { currentUser, refreshCurrentUser } = useAuth();
  const { refreshOrganization } = useOrganization();

  // Separate effect for initial user evaluation - only runs when userId changes
  useEffect(() => {
    const userId = currentUser?.uid ?? null;

    if (!currentUser || !userId) {
      setIsOnboardingComplete(false);
      setLoading(false);
      initializedUserIdRef.current = null;
      lastEvaluatedValuesRef.current = { userId: null, orgId: null, firstName: null, lastName: null };
      return;
    }
    
    // Only run initialization logic if this is a new user
    if (initializedUserIdRef.current !== userId) {
      const hasOrgId = !!currentUser.orgId;
      const hasProfileInfo = !!(currentUser.firstName && currentUser.lastName);
      const hasCompleted = hasOrgId && hasProfileInfo;
      
      setIsOnboardingComplete(hasCompleted);
      lastEvaluatedValuesRef.current = {
        userId,
        orgId: currentUser.orgId,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      };

      if (hasCompleted) {
        setCurrentStep('complete');
      } else if (!hasOrgId) {
        setCurrentStep('initial');
      } else {
        setCurrentStep('profile');
      }
      initializedUserIdRef.current = userId;
      setLoading(false);
    }
  }, [currentUser?.uid, currentUser]); // Only depend on userId, not the entire user object

  // Separate effect for updating completion status when user data changes
  useEffect(() => {
    const userId = currentUser?.uid ?? null;
    
    // Don't run if we're in the middle of an update operation
    if (isUpdatingRef.current) {
      return;
    }
    
    // Only update completion status if user is already initialized
    if (!currentUser || !userId || initializedUserIdRef.current !== userId) {
      return;
    }
    
    const hasOrgId = !!currentUser.orgId;
    const hasProfileInfo = !!(currentUser.firstName && currentUser.lastName);
    const hasCompleted = hasOrgId && hasProfileInfo;
    
    const lastValues = lastEvaluatedValuesRef.current;
    const valuesChanged = 
      lastValues.orgId !== currentUser.orgId ||
      lastValues.firstName !== currentUser.firstName ||
      lastValues.lastName !== currentUser.lastName;
    
    if (valuesChanged) {
      setIsOnboardingComplete(hasCompleted);
      lastEvaluatedValuesRef.current = {
        userId,
        orgId: currentUser.orgId,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      };
    }
  }, [currentUser?.uid, currentUser?.orgId, currentUser?.firstName, currentUser?.lastName, currentUser]);

  const startOnboarding = () => {
    setCurrentStep('initial');
  };

  const completeOnboarding = async () => {
    setIsOnboardingComplete(true);
    if (typeof refreshOrganization === 'function') {
      await refreshOrganization();
    }
    navigate('/dashboard');
  };

  const nextStep = async () => {
    switch (currentStep) {
      case 'initial':
        // This should not happen - initial step should go to create/join
        break;
      case 'create':
      case 'join':
        setCurrentStep('profile');
        break;
      case 'profile':
        await completeOnboarding();
        break;
      case 'complete':
        await completeOnboarding();
        break;
    }
  };

  const previousStep = () => {
    switch (currentStep) {
      case 'profile':
        setCurrentStep('initial');
        break;
      case 'create':
      case 'join':
        setCurrentStep('initial');
        break;
      case 'complete':
        setCurrentStep('profile');
        break;
    }
  };

  const handleCreateOrganization = async (organization: Organization) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const now = new Date().toISOString();

      const { error: orgError } = await supabase.from('organizations').insert({
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
        type: organization.type,
        status: organization.status,
        join_code: organization.joinCode,
        base_airport: organization.baseAirport,
        contact: organization.contact,
        settings: organization.settings,
        owner_uid: currentUser.uid,
        created_at: now,
        updated_at: now,
        logo: organization.logo || null,
        description: organization.description || null,
        social_media: organization.socialMedia || null
      });

      if (orgError) {
        throw orgError;
      }

      const { error: memberError } = await supabase.rpc('add_self_to_organization', {
        p_org_id: organization.id,
        p_role: 'admin',
      });

      if (memberError) {
        throw memberError;
      }

      const { error: userError } = await supabase
        .from('users')
        .update({
          org_id: organization.id,
          role: 'admin',
          updated_at: now
        })
        .eq('id', currentUser.uid);

      if (userError) {
        throw userError;
      }

      // Lock the step first to prevent useEffect from changing it
      initializedUserIdRef.current = currentUser.uid;
      isUpdatingRef.current = true; // Prevent useEffect from interfering
      setCurrentStep('profile');
      setLoading(false);
      
      // Update local user state immediately to reflect the new orgId and admin role
      // We've already updated the DB, so just sync the local state
      if (currentUser) {
        // Update the user state directly without making another DB call
        // The refreshCurrentUser will fetch the latest data from DB
        if (typeof refreshCurrentUser === 'function') {
          refreshCurrentUser()
            .finally(() => {
              // Clear the flag after a short delay to allow state to settle
              setTimeout(() => {
                isUpdatingRef.current = false;
              }, 100);
            })
            .catch(err => logger.error('Error refreshing user state:', err));
        } else {
          isUpdatingRef.current = false;
        }
      } else {
        isUpdatingRef.current = false;
      }
      
      // Refresh organization context in the background
      if (typeof refreshOrganization === 'function') {
        refreshOrganization().catch(err => logger.error('Error refreshing org:', err));
      }
    } catch (error) {
      setLoading(false);
      logger.error('Error creating organization:', error);
      throw error;
    }
  };

  const handleJoinOrganization = async (code: string) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      // Fetch full organization data including type.
      // Uses a SECURITY DEFINER RPC: org RLS only lets members read their own
      // org, so a direct select by join_code always returns zero rows for the
      // prospect doing the joining (see migration 20260709153000).
      const { data: orgData, error } = await supabase
        .rpc('get_organization_by_join_code', { p_code: code })
        .maybeSingle<{
          id: string;
          type: string;
          name: string;
          description: string | null;
          base_airport: string | null;
          join_code: string;
        }>();

      if (error) {
        throw error;
      }

      if (!orgData) {
        setLoading(false);
        throw new Error('Invalid invitation code');
      }

      // For flying clubs, we need approval - return org data so JoinModal can show request form
      if (orgData.type === 'club') {
        setLoading(false);
        // Return organization data - JoinModal will handle showing the request form
        return {
          orgId: orgData.id,
          organizationType: orgData.type,
          organizationName: orgData.name,
          organizationDescription: orgData.description ?? undefined,
          baseAirport: orgData.base_airport ?? undefined,
          joinCode: orgData.join_code,
          requiresApproval: true,
        };
      }

      // For non-clubs (schools), proceed with instant join
      const now = new Date().toISOString();

      const { error: membershipError } = await supabase.from('organization_members').upsert({
        org_id: orgData.id,
        user_id: currentUser.uid,
        joined_at: now
      });

      if (membershipError) {
        throw membershipError;
      }

      const { error: userError } = await supabase
        .from('users')
        .update({
          org_id: orgData.id,
          role: 'member',
          updated_at: now
        })
        .eq('id', currentUser.uid);

      if (userError) {
        throw userError;
      }

      // Lock the step first to prevent useEffect from changing it
      initializedUserIdRef.current = currentUser.uid;
      isUpdatingRef.current = true; // Prevent useEffect from interfering
      setCurrentStep('profile');
      setLoading(false);
      
      // Update local user state immediately to reflect the new orgId
      // We've already updated the DB, so just sync the local state
      if (currentUser) {
        // Update the user state directly without making another DB call
        // The refreshCurrentUser will fetch the latest data from DB
        if (typeof refreshCurrentUser === 'function') {
          refreshCurrentUser()
            .finally(() => {
              // Clear the flag after a short delay to allow state to settle
              setTimeout(() => {
                isUpdatingRef.current = false;
              }, 100);
            })
            .catch(err => logger.error('Error refreshing user state:', err));
        } else {
          isUpdatingRef.current = false;
        }
      } else {
        isUpdatingRef.current = false;
      }
      
      // Refresh organization context in the background
      if (typeof refreshOrganization === 'function') {
        refreshOrganization().catch(err => logger.error('Error refreshing org:', err));
      }
    } catch (error) {
      setLoading(false);
      logger.error('Error joining organization:', error);
      throw error;
    }
  };

  const handleUpdateProfile = async (userData: Partial<User>) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: userData.firstName ?? currentUser.firstName,
          last_name: userData.lastName ?? currentUser.lastName,
          phone_number: userData.phoneNumber ?? currentUser.phoneNumber,
          address: userData.address ?? currentUser.address,
          emergency_contact: userData.emergencyContact ?? currentUser.emergencyContact,
          role: userData.role ?? currentUser.role,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentUser.uid);

      if (updateError) {
        throw updateError;
      }
      if (typeof refreshCurrentUser === 'function') {
        await refreshCurrentUser();
      }
      setLoading(false);
      await nextStep();
    } catch (error) {
      setLoading(false);
      logger.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    currentStep,
    isOnboardingComplete,
    loading,
    startOnboarding,
    setCurrentStep,
    nextStep,
    previousStep,
    handleCreateOrganization,
    handleJoinOrganization,
    handleUpdateProfile,
    completeOnboarding,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
} 